import { IPendingCallerResItem, ICustomerApptItem } from 'services/types/caller';
import { IStaffUI } from 'types/staff';
import { ICallerCallItem, ICallerHistoryEntry } from './types';

// Display name for a caller — customers not in the system have no name.
export const UNKNOWN_CUSTOMER = 'Unknown Customer';
export const callerDisplayName = (item: Pick<ICallerCallItem, 'name'>) => item.name || UNKNOWN_CUSTOMER;

// Default page size for the booking-history modal; server has no cap, keep it sane.
export const DEFAULT_PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 50;
export const clampPageSize = (size: number) =>
  Math.min(MAX_PAGE_SIZE, Math.max(1, Math.floor(size) || DEFAULT_PAGE_SIZE));

// API times are pre-formatted "hh:mm a MM-dd-yyyy" (e.g. "02:30 PM 07-01-2026").
// Split on the last space → date = last token, time = everything before it.
// Never re-parse by timezone.
export const splitApiDateTime = (s: string): { date: string; time: string } => {
  const trimmed = (s || '').trim();
  const idx = trimmed.lastIndexOf(' ');
  if (idx < 0) return { date: trimmed, time: '' };
  return { time: trimmed.slice(0, idx).trim(), date: trimmed.slice(idx + 1).trim() };
};

// Pending caller (GET /pending-caller-id) → list-card view-model.
// "New" callers have no name → blank name, badge hidden (nameAvailable=false).
export const mapPendingToCallItem = (item: IPendingCallerResItem): ICallerCallItem => ({
  id: item.id,
  name: item.customerName || '',
  phone: item.customerPhone || '',
  callTimerText: item.time || '',
  nameAvailable: item.customerType === 'Regular',
  customerId: item.customerId != null ? String(item.customerId) : undefined,
});

// Appointment item (recent or paginated) → history entry. TOTAL is not provided
// by these APIs, so it stays undefined (that column is still out of the modal).
// The staff name/avatar are not in the response either — they come from the shop
// staff store, passed in so this stays a pure mapper. Deliberately NOT filtered
// through isBookableStaff: this is a display lookup, and a Maintenance staff must
// still show their own name rather than falling back to "Anyone".
export const mapApptItemToHistoryEntry = (
  item: ICustomerApptItem,
  staffsById?: Record<string, IStaffUI>,
): ICallerHistoryEntry => {
  const { date, time } = splitApiDateTime(item.startTime);
  // A falsy staffId (0/null, or absent on an older backend) means the visit was
  // booked as "Anyone" — there is no staff to carry over into a re-book.
  const staffId = item.staffId ? String(item.staffId) : undefined;
  const found = staffId ? staffsById?.[staffId] : undefined;
  return {
    date,
    time,
    services: (item.serviceNames || []).join(', '),
    note: item.note ?? undefined,
    staffId,
    // No staffId → the visit was "Anyone". An id that resolves to nothing means the
    // staff was deleted; leave it undefined and let each view decide how to show that.
    staff: staffId ? found?.name : 'Anyone',
    staffAvatar: found?.avatar,
  };
};
